import { useEffect, useMemo, useRef } from 'react';
import { taskDepartmentResolver } from '@battleops/shared';
import { useDepartments } from '../context/DepartmentContext';
import { useTasks } from '../context/TaskContext';
import { useProjects } from '../context/ProjectContext';

// The switcher follows the work (Lj 2026-10-01: "the switcher should follow the plan"): when you START a task during
// the session, this device's department moves to that task's. The app opens on All (Lj 2026-10-10: "every time I
// update the default department is General but it should be All") — so it doesn't jump at launch, and Up next no
// longer moves it (that's what put it on General). Switching by hand still works; it holds until you start another task.
// Mounted once, inside every provider (mobile App.js, web AppRoutes). Renders nothing.
export function DepartmentFollowsPlan() {
  const { departments, departmentActive, departmentLocked, setCurrentDepartment } = useDepartments();
  const { tasks, activeTaskId, loaded, timerRestored } = useTasks();
  const { projects } = useProjects();

  const head = useMemo(() => {
    if (!departmentActive || departmentLocked || !activeTaskId) return null;
    return tasks.find((t) => t.id === activeTaskId) || null;   // the task you're on — not Up next
  }, [departmentActive, departmentLocked, activeTaskId, tasks]);

  const deptId = useMemo(() => (head ? taskDepartmentResolver(tasks, departments, projects)(head) : null), [head, tasks, departments, projects]);
  // The launch baseline is taken only once the tasks AND meta/timer have been read (Lj, Oct 16: a web refresh put him
  // back on General — the first look landed before the timer doc loaded, so the running task then read as a fresh START).
  const settled = loaded && timerRestored;
  const last = useRef(undefined);   // undefined = the first settled look at launch: note it, don't switch
  useEffect(() => {
    if (!settled) return;
    const key = head && deptId ? `${head.id}:${deptId}` : null;
    if (last.current === undefined) { last.current = key; return; }
    if (!key || last.current === key) return;
    last.current = key;
    setCurrentDepartment(deptId);
  }, [settled, head, deptId, setCurrentDepartment]);
  return null;
}
